/**
 * Component gallery, served at /__ui.
 *
 * It exists so the designer and the client can look at the design system on its own,
 * without hunting for a page that happens to use a given component. It is deliberately
 * unlinked from the navigation and marked `noindex`.
 *
 * Everything here is rendered from the real tokens and the real components. If a swatch
 * looks wrong, the token is wrong - do not fix it in this file.
 */
import { useEffect } from 'react';
import { Button, BUTTON_SHAPE, BUTTON_STATE_PREVIEW } from '../components/ui/Button';
import { Checkbox, Field, Select, TextArea, TextInput } from '../components/ui/Field';
import { Icon, ICONS, type IconName } from '../components/ui/Icon';
import { applyHead } from '../lib/head';

// No `Visited`: the file defines one and it is deliberately not wired to `:visited`.
// See the note in Button.tsx.
const STATES = ['Default', 'Hover', 'Active', 'Focused', 'Disabled'];

const COLOUR_SCALES: { name: string; note: string; steps: [string, string][] }[] = [
  {
    name: 'Blue',
    note: 'Color/Blue',
    steps: [['50', 'bg-blue-50'], ['100', 'bg-blue-100'], ['200', 'bg-blue-200'], ['300', 'bg-blue-300'], ['400', 'bg-blue-400'], ['500', 'bg-blue-500'], ['600', 'bg-blue-600'], ['700', 'bg-blue-700'], ['800', 'bg-blue-800'], ['900', 'bg-blue-900']]
  },
  {
    name: 'Blue-Dark',
    note: 'Color/Blue-Dark - the interactive scale used by Button',
    steps: [['200', 'bg-blue-dark-200'], ['300', 'bg-blue-dark-300'], ['400', 'bg-blue-dark-400'], ['500', 'bg-blue-dark-500']]
  },
  {
    name: 'Grey',
    note: 'Color/Grey. 950 is Figma’s Grey-900-A',
    steps: [['50', 'bg-grey-50'], ['75', 'bg-grey-75'], ['100', 'bg-grey-100'], ['200', 'bg-grey-200'], ['300', 'bg-grey-300'], ['400', 'bg-grey-400'], ['500', 'bg-grey-500'], ['600', 'bg-grey-600'], ['700', 'bg-grey-700'], ['800', 'bg-grey-800'], ['900', 'bg-grey-900'], ['950', 'bg-grey-950']]
  },
  {
    name: 'Orange',
    note: 'Color/Orange. 100, 300 and 400 are missing from the file',
    steps: [['50', 'bg-orange-50'], ['200', 'bg-orange-200'], ['500', 'bg-orange-500'], ['600', 'bg-orange-600'], ['700', 'bg-orange-700'], ['800', 'bg-orange-800'], ['900', 'bg-orange-900']]
  },
  {
    name: 'Green',
    note: 'Color/Green',
    steps: [['50', 'bg-green-50'], ['100', 'bg-green-100'], ['200', 'bg-green-200'], ['300', 'bg-green-300'], ['400', 'bg-green-400'], ['500', 'bg-green-500'], ['600', 'bg-green-600'], ['700', 'bg-green-700'], ['800', 'bg-green-800'], ['900', 'bg-green-900']]
  }
];

const SEMANTIC: [string, string, string][] = [
  ['ink', 'bg-ink', 'headings, strong copy'],
  ['ink-muted', 'bg-ink-muted', 'body copy'],
  ['ink-faint', 'bg-ink-faint', 'meta, captions'],
  ['ink-strong', 'bg-ink-strong', 'rare high contrast'],
  ['surface', 'bg-surface', 'the page'],
  ['surface-subtle', 'bg-surface-subtle', 'barely-there band'],
  ['surface-muted', 'bg-surface-muted', 'cards, quiet sections'],
  ['surface-sunken', 'bg-surface-sunken', 'media placeholders'],
  ['surface-dark', 'bg-surface-dark', 'inverted sections'],
  ['surface-brand', 'bg-surface-brand', 'brand-filled blocks']
];

const TYPE_SCALE: [string, string, string][] = [
  ['text-display', 'text-display', 'Display-01 - 82 / 1.10'],
  ['text-h1', 'text-h1', 'h1 - 60 / 1.16'],
  ['text-h2', 'text-h2', 'h2 - 46 / 1.22'],
  ['text-h3', 'text-h3', 'h3 - 34 / 1.17'],
  ['text-h4', 'text-h4', 'h4 - 26 / 1.30'],
  ['text-h5', 'text-h5', 'h5 - 20 / 1.20'],
  ['text-body-md', 'text-body-md', 'body-medium - 18 / 1.40'],
  ['text-body', 'text-body', 'body - 16 / 1.50'],
  ['text-body-sm', 'text-body-sm', 'body-small - 14 / 1.40'],
  ['text-caption', 'text-caption', 'caption - 12 / 1.40']
];

const RADII: [string, string][] = [
  ['rounded-xs', 'rounded-xs'], ['rounded-s', 'rounded-s'], ['rounded-m', 'rounded-m'],
  ['rounded-l', 'rounded-l'], ['rounded-xl', 'rounded-xl'], ['rounded-xxxl', 'rounded-xxxl'],
  ['rounded-xxxl2', 'rounded-xxxl2'], ['rounded-full', 'rounded-full']
];

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-grey-300 py-12">
      <h2 className="text-h4 font-medium text-ink">{title}</h2>
      {note ? <p className="mt-1 max-w-3xl text-body-sm text-ink-faint">{note}</p> : null}
      <div className="mt-8">{children}</div>
    </section>
  );
}

export default function GalleryPage() {
  useEffect(() => {
    applyHead({ title: 'Weco-Travel - component gallery', noindex: true });
  }, []);

  return (
    <div className="weco-container py-16">
      <header className="pb-8">
        <p className="text-caption font-semibold uppercase text-ink-faint">Weco-Travel</p>
        <h1 className="mt-2 text-h2 font-medium text-ink">Component gallery</h1>
        <p className="mt-3 max-w-3xl text-body-md text-ink-muted">
          The design system as it stands: tokens read from the Figma variables, and the
          components built on them. Not a page of the website - it is unlinked and{' '}
          <code className="rounded-xs bg-surface-muted px-1.5 py-0.5 text-body-sm">noindex</code>.
          Open questions for the designer are noted in the sections below.
        </p>
      </header>

      <Section title="Colour - raw scales" note="Named exactly as in Figma, so a hand-off can be followed literally.">
        <div className="flex flex-col gap-8">
          {COLOUR_SCALES.map((scale) => (
            <div key={scale.name}>
              <h3 className="text-body font-medium text-ink">{scale.name}</h3>
              <p className="text-body-sm text-ink-faint">{scale.note}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {scale.steps.map(([step, cls]) => (
                  <div key={step} className="w-20">
                    <div className={`h-14 w-full rounded-s border border-grey-300 ${cls}`} />
                    <p className="mt-1 text-caption text-ink-faint">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Colour - semantic" note="What components actually use. A re-brand changes these and nothing else.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {SEMANTIC.map(([name, cls, use]) => (
            <div key={name}>
              <div className={`h-14 w-full rounded-s border border-grey-300 ${cls}`} />
              <p className="mt-1 text-body-sm text-ink">{name}</p>
              <p className="text-caption text-ink-faint">{use}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Typography"
        note="Archivo, self-hosted. Each token carries its own line height and letter spacing - never add leading-* or tracking-* next to one. The file is desktop-only, so a section that has to shrink should step down inside this scale rather than invent a size."
      >
        <div className="flex flex-col gap-6">
          {TYPE_SCALE.map(([name, cls, note]) => (
            <div key={name} className="flex flex-col gap-1 border-b border-grey-200 pb-5 lg:flex-row lg:items-baseline lg:gap-8">
              <div className="w-48 shrink-0">
                <p className="text-body-sm text-ink">{name}</p>
                <p className="text-caption text-ink-faint">{note}</p>
              </div>
              <p className={`${cls} text-ink`}>Podróże służbowe bez kompromisów</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Radius" note="Radius/xxl is not defined in the file, so it does not exist here either.">
        <div className="flex flex-wrap gap-4">
          {RADII.map(([name, cls]) => (
            <div key={name} className="w-32">
              <div className={`h-20 w-full border border-grey-400 bg-surface-muted ${cls}`} />
              <p className="mt-1 text-caption text-ink-faint">{name}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Button"
        note="Figma 494:2861. The top block is interactive - hover, tab to it and hold the mouse down to see the real states. The table below forces each state so it can be compared with the Figma frame. The file also defines a Visited state; it is deliberately not applied, because on a filled button it turned every call to action navy once its target had been opened. One open question remains: the 6px grey-600 focus ring is below the 3:1 contrast WCAG 2.2 asks for."
      >
        <div className="flex flex-wrap items-center gap-4">
          <Button href="#gallery">Button</Button>
          <Button href="#gallery" withIcon>Button</Button>
          <Button variant="secondary" href="#gallery">Button</Button>
          <Button variant="secondary" href="#gallery" withIcon>Button</Button>
          <Button disabled>Disabled</Button>
          <Button variant="secondary" disabled>Disabled</Button>
        </div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full min-w-[760px] border-separate border-spacing-3">
            <thead>
              <tr>
                <th className="w-24" />
                {STATES.map((state) => (
                  <th key={state} className="text-left text-caption font-semibold uppercase text-ink-faint">{state}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(['primary', 'secondary'] as const).map((variant) => (
                <tr key={variant}>
                  <th className="text-left align-middle text-body-sm font-medium text-ink">{variant}</th>
                  {STATES.map((state) => (
                    <td key={state}>
                      <span className={`${BUTTON_SHAPE} ${BUTTON_STATE_PREVIEW[variant][state]}`}>Button</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title="Form fields"
        note="Figma 701:4006. The file disagrees with itself - two label sizes, and a green-50 select next to white text inputs - so one label style and one control fill are used throughout. Focus, error and disabled states for inputs are not designed at all; what is here is a placeholder."
      >
        <form className="grid max-w-3xl gap-6" onSubmit={(e) => e.preventDefault()}>
          <Field id="g-department" label="Wybierz odpowiedni dział/departament">
            <Select id="g-department" defaultValue="">
              <option value="" disabled>Dział…</option>
              <option value="business-travel">Podróże służbowe</option>
              <option value="meetings">Spotkania i wyjazdy motywacyjne</option>
            </Select>
          </Field>

          <div className="grid gap-6 sm:grid-cols-2">
            <Field id="g-person" label="Osoba kontaktowa" required>
              <TextInput id="g-person" placeholder="Imię i nazwisko" />
            </Field>
            <Field id="g-email" label="E-mail" required error="Podaj poprawny adres e-mail.">
              <TextInput id="g-email" type="email" aria-invalid placeholder="jan@firma.pl" />
            </Field>
          </div>

          <Field id="g-message" label="W czym możemy pomóc?" required>
            <TextArea id="g-message" rows={5} placeholder="Wiadomość" />
          </Field>

          <Checkbox id="g-consent" label="Zgadzam się z polityką prywatności." />

          <div>
            <Button type="submit" withIcon>Wyślij</Button>
          </div>
        </form>
      </Section>

      <Section title="Icons" note="Exported from the Figma icon/All set and committed as SVG. Only the two the components need so far - the rest of the set follows the same route.">
        <div className="flex flex-wrap gap-6">
          {(Object.keys(ICONS) as IconName[]).map((name) => (
            <div key={name} className="flex w-32 flex-col items-center gap-2 rounded-s border border-grey-300 p-4">
              <Icon name={name} size={24} className="text-ink" />
              <p className="text-caption text-ink-faint">{name}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Not built yet" note="The rest of milestone M2.">
        <ul className="flex list-disc flex-col gap-2 pl-5 text-body text-ink-muted">
          <li>The full icon set - `icon/All` has around sixty glyphs; two are in.</li>
          <li>Card and `Flag` components, and the `Foto/*` media frames.</li>
          <li>Written-down responsive rules per primitive.</li>
        </ul>
      </Section>
    </div>
  );
}
